'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/app/Components/AuthProvider';
import { useOnboardingStatus } from '@/app/hooks/useOnboardingStatus';
import { supabase } from '@/app/lib/supabase';
import { useRouter } from 'next/navigation';
import { degreesApi } from '@/app/services/api';
import type { Degree } from '@/app/types/interfaces';
import Modal from '@/app/Components/Modal';
import { 
  AcademicCapIcon, 
  UserIcon,
  InformationCircleIcon,
  XMarkIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline';

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type OnboardingStep = 'major' | 'year' | 'complete';

export default function OnboardingModal({ isOpen, onClose }: OnboardingModalProps) {
  const { user } = useAuth();
  const { markOnboardingComplete, skipOnboarding, isLoading } = useOnboardingStatus();
  const router = useRouter();
  
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('major');
  const [formData, setFormData] = useState<{
    year: string;
    majors: string[];
  }>({
    year: '',
    majors: [],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [allDegrees, setAllDegrees] = useState<Degree[]>([]);
  const [majorSearch, setMajorSearch] = useState('');

  useEffect(() => {
    const fetchDegrees = async () => {
      try {
        const degrees = await degreesApi.getAll();
        setAllDegrees(degrees);
      } catch (error) {
        console.error("Failed to fetch degrees:", error);
      }
    };
    fetchDegrees();
  }, []);

  const years = [
    'Freshman',
    'Sophomore', 
    'Junior',
    'Senior',
    'Graduate Student',
    'Other'
  ];

  const steps: { key: OnboardingStep; title: string; description: string }[] = [
    { key: 'major', title: 'Major(s)', description: 'What are you studying?' },
    { key: 'year', title: 'Academic Year', description: 'What year are you in?' },
    { key: 'complete', title: 'Setup Complete!', description: 'You can improve your plan with a transcript import.' }
  ];

  const currentStepIndex = steps.findIndex(step => step.key === currentStep);

  const filteredDegrees = useMemo(() => {
    if (!majorSearch) return [];
    
    const degrees = allDegrees;
    
    return degrees.filter(degree => 
        degree.Name.toLowerCase().includes(majorSearch.toLowerCase()) &&
        !formData.majors.includes(degree.Name)
    ).slice(0, 10);
  }, [majorSearch, allDegrees, formData.majors]);

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateCurrentStep = () => {
    const newErrors: Record<string, string> = {};
    
    switch (currentStep) {
      case 'major':
        if (formData.majors.length === 0) newErrors.majors = 'Please select at least one major';
        break;
      case 'year':
        if (!formData.year) newErrors.year = 'Please select your year';
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) {
      return;
    }

    const stepOrder: OnboardingStep[] = ['major', 'year', 'complete'];
    const currentIndex = stepOrder.indexOf(currentStep);
    
    if (currentIndex < stepOrder.length - 1) {
      const nextStep = stepOrder[currentIndex + 1];
      setCurrentStep(nextStep);
      
      if (nextStep === 'complete') {
        handleAutoSave();
      }
    }
  };

  const handleAutoSave = async () => {
    try {
      setIsAutoSaving(true);
      await markOnboardingComplete({ year: formData.year, majors: formData.majors });
    } catch (error) {
      console.error('Error auto-saving onboarding:', error);
    } finally {
      setIsAutoSaving(false);
    }
  };

  const [isAutoSaving, setIsAutoSaving] = useState(false);

  const handleBack = () => {
    const stepOrder: OnboardingStep[] = ['major', 'year', 'complete'];
    const currentIndex = stepOrder.indexOf(currentStep);
    
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  };

  const handleSkip = async () => {
    try {
      await skipOnboarding();
      onClose();
    } catch (error) {
      console.error('Error skipping onboarding:', error);
    }
  };

  const handleGoToPrerequisites = () => {
    onClose();
    router.push('/prerequisite-checker');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'major':
        return (
            <div className="space-y-4">
              <div>
                <label htmlFor="onboarding-major-search" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  <AcademicCapIcon className="h-4 w-4 inline mr-2" />
                  Search for your Major(s)
                </label>
                <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                        id="onboarding-major-search"
                        type="text"
                        value={majorSearch}
                        onChange={(e) => setMajorSearch(e.target.value)}
                        placeholder="e.g., Computer Science, Biology..."
                        className="w-full pl-10 pr-3 py-2 border rounded-control focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                </div>
                {errors.majors && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.majors}</p>
                )}

                {/* Search Results */}
                {filteredDegrees.length > 0 && (
                    <div className="mt-2 border border-slate-200 dark:border-slate-700 rounded-control max-h-40 overflow-y-auto">
                        {filteredDegrees.map(degree => (
                            <div key={degree.ID} 
                                 onClick={() => {
                                     handleInputChange('majors', [...formData.majors, degree.Name]);
                                     setMajorSearch('');
                                 }}
                                 className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700">
                                <span>{degree.Name}</span>
                                <PlusCircleIcon className="h-5 w-5 text-slate-400" />
                            </div>
                        ))}
                    </div>
                )}

                {/* Selected Majors */}
                {formData.majors.length > 0 && (
                    <div className="mt-4">
                        <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Selected Majors:</h4>
                        <div className="flex flex-wrap gap-2">
                            {formData.majors.map(major => (
                                <div key={major} className="flex items-center bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200 text-sm font-medium px-2 py-1 rounded-full">
                                    <span>{major}</span>
                                    <button type="button" aria-label={`Remove ${major}`} onClick={() => handleInputChange('majors', formData.majors.filter(m => m !== major))} className="ml-2 min-h-12 min-w-12 md:min-h-0 md:min-w-0">
                                        <XMarkIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
              </div>
            </div>
          );

      case 'year':
        return (
          <div className="space-y-4">
            <div>
              <label htmlFor="onboarding-academic-year" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <UserIcon className="h-4 w-4 inline mr-2" />
                Academic Year
              </label>
              <select
                id="onboarding-academic-year"
                value={formData.year}
                onChange={(e) => handleInputChange('year', e.target.value)}
                className={`w-full px-3 py-2 border rounded-control focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-slate-700 dark:border-slate-600 dark:text-white ${
                  errors.year ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
                }`}
              >
                <option value="">Select your year</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              {errors.year && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.year}</p>
              )}
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="space-y-6">
            <div className="text-center">
              {isAutoSaving ? (
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 mb-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                </div>
              ) : (
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                  <CheckIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
              )}
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                Welcome to Scarlet Sync!
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {isAutoSaving 
                  ? 'Saving your profile...' 
                  : 'Your profile has been set up successfully and saved automatically. You\'re now ready to start planning your academic journey.'
                }
              </p>
            </div>
            
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-control p-4">
              <div className="flex items-start space-x-3">
                <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">Want to get better course recommendations?</p>
                  <p>Upload a transcript to pre-fill completed courses and degree progress. You review matches before anything is saved.</p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      showCloseButton={false}
      closeOnOverlayClick={false}
      ariaLabel={steps[currentStepIndex]?.title || 'Welcome to Scarlet Sync'}
    >
        <div>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 dark:bg-red-900/30 p-2 rounded-control">
                <AcademicCapIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  {steps[currentStepIndex]?.title || 'Welcome to Scarlet Sync!'}
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {steps[currentStepIndex]?.description || 'Let\'s get to know you better'}
                </p>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close onboarding"
              onClick={onClose}
              className="grid min-h-12 min-w-12 place-items-center text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Step {currentStepIndex + 1} of {steps.length}
              </span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {Math.round(((currentStepIndex + 1) / steps.length) * 100)}%
              </span>
            </div>
            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
              <div 
                className="bg-red-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStepIndex + 1) / steps.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Step Content */}
          <div className="mb-6">
            {renderStepContent()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center">
            {currentStep === 'major' ? (
              <button
                type="button"
                onClick={handleSkip}
                disabled={isLoading}
                className="min-h-12 px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
              >
                Skip for now
              </button>
            ) : (
              <button
                type="button"
                onClick={handleBack}
                disabled={isLoading}
                className="flex min-h-12 items-center px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                Back
              </button>
            )}

            {currentStep === 'complete' ? (
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleGoToPrerequisites}
                  disabled={isAutoSaving}
                  className="min-h-12 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-control transition-colors duration-200"
                >
                  Import transcript
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isAutoSaving}
                  className="min-h-12 px-4 py-2 bg-slate-600 hover:bg-slate-700 disabled:bg-slate-400 text-white font-medium rounded-control transition-colors duration-200"
                >
                  {isAutoSaving ? 'Saving...' : 'Continue to App'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                disabled={isLoading}
                className="flex min-h-12 items-center px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium rounded-control transition-colors duration-200"
              >
                Next
                <ArrowRightIcon className="h-4 w-4 ml-1" />
              </button>
            )}
          </div>
        </div>
    </Modal>
  );
}
